/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { SurveyUserRoles } from '@constants/user';
import { InitiativePermissions } from './InitiativePermissions';
import { PermissionsSurvey, PermissionsUser } from './types';
import { WorkgroupPermissions } from './WorkgroupPermissions';

export class SurveyPermissions {
  static canContributeRoles = [SurveyUserRoles.Stakeholder];
  static canVerifyRoles = [SurveyUserRoles.Verifier];
  static canManageRoles = [SurveyUserRoles.Admin];
  static canAccessAllDataRoles = [SurveyUserRoles.Stakeholder, SurveyUserRoles.Verifier, SurveyUserRoles.Admin];

  public static canManage(
    survey: Pick<PermissionsSurvey, 'initiativeId' | 'roles' | 'workgroups'>,
    user: PermissionsUser,
  ): boolean {
    const userId = String(user._id);
    if (survey.roles?.admin?.some((adminId) => String(adminId) === userId)) {
      return true;
    }

    if (
      WorkgroupPermissions.checkHasSurveyUserRoles({
        survey,
        userId: user._id,
        roles: SurveyPermissions.canManageRoles,
      })
    ) {
      return true;
    }

    return InitiativePermissions.canManageInitiative(user, survey.initiativeId);
  }

  public static canAccessAllData(
    survey: Pick<PermissionsSurvey, 'initiativeId' | 'stakeholders' | 'roles' | 'workgroups'>,
    user: PermissionsUser,
  ): boolean {
    const userId = user._id;

    // Check if user has survey-level permissions
    if (survey.stakeholders.stakeholder?.find((id) => id.toString() === userId)) {
      return true;
    }
    if (survey.stakeholders.verifier?.find((id) => id.toString() === userId)) {
      return true;
    }
    if (survey.roles?.admin?.some((adminId) => String(adminId) === userId)) {
      return true;
    }

    if (
      WorkgroupPermissions.checkHasSurveyUserRoles({
        survey,
        userId: user._id,
        roles: SurveyPermissions.canAccessAllDataRoles,
      })
    ) {
      return true;
    }

    // Check if user has initiative permission
    if (InitiativePermissions.canAccessAllSurveyData(user, survey.initiativeId)) {
      return true;
    }

    return false;
  }

  public static canContribute(
    survey: Pick<PermissionsSurvey, 'initiativeId' | 'stakeholders' | 'roles' | 'workgroups'>,
    user: PermissionsUser,
  ): boolean {
    const userId = user._id;

    // Check if user has survey permission
    if (survey.stakeholders.stakeholder?.find((id) => id === userId)) {
      return true;
    }

    if (survey.roles?.admin?.some((adminId) => String(adminId) === userId)) {
      return true;
    }

    if (
      WorkgroupPermissions.checkHasSurveyUserRoles({
        survey,
        userId: user._id,
        roles: SurveyPermissions.canContributeRoles,
      })
    ) {
      return true;
    }

    // Check if user has initiative permission
    return InitiativePermissions.canContribute(user, survey.initiativeId);
  }

  public static canVerify(
    survey: Pick<PermissionsSurvey, 'initiativeId' | 'stakeholders' | 'roles' | 'workgroups'>,
    user: PermissionsUser,
  ): boolean {
    const userId = user._id;

    // Check if user has survey permission
    if (survey.stakeholders.verifier.find((id) => id === userId)) {
      return true;
    }

    if (survey.roles?.admin?.some((adminId) => String(adminId) === userId)) {
      return true;
    }

    if (
      WorkgroupPermissions.checkHasSurveyUserRoles({
        survey,
        userId: user._id,
        roles: SurveyPermissions.canVerifyRoles,
      })
    ) {
      return true;
    }

    // Check if user has initiative permission
    return InitiativePermissions.canVerify(user, survey.initiativeId);
  }
}
